<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>公告管理</h1>
        <p>{{ scopeHint }}</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增公告
        </el-button>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索标题关键词"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select
        v-model="listQuery.schoolId"
        placeholder="全部学校"
        clearable
        class="school-select"
        @change="handleFilter"
      >
        <el-option v-for="item in schoolOptions" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-select v-model="listQuery.status" placeholder="全部状态" clearable class="school-select" @change="handleFilter">
        <el-option label="已发布" :value="1" />
        <el-option label="草稿" :value="0" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />

        <el-table-column label="标题" min-width="220">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.title || '未命名公告' }}</div>
            <div class="cell-sub announcement-content">{{ row.content }}</div>
          </template>
        </el-table-column>

        <!-- 这一列是列表的信息重心：一条公告发给了谁，扫一眼就要看清 -->
        <el-table-column label="推送范围" width="190">
          <template slot-scope="{ row }">
            <div class="scope-chips">
              <span v-if="isAllScope(row)" class="schip schip--all">全站</span>
              <template v-else>
                <span v-for="name in row.schoolNames.slice(0, 2)" :key="name" class="schip">{{ name }}</span>
                <el-tooltip v-if="row.schoolNames.length > 2" :content="row.schoolNames.join('、')">
                  <span class="schip schip--more">+{{ row.schoolNames.length - 2 }}</span>
                </el-tooltip>
                <span v-if="!row.schoolNames.length" class="schip schip--none">未选学校</span>
              </template>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="发布人" width="110">
          <template slot-scope="{ row }">
            <span class="cell-sub publisher-name">{{ row.publisherName || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.status === 1 ? 's1' : 's0'">
              <i />{{ row.status === 1 ? '已发布' : '草稿' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="发布时间" width="160">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.publishedAt ? formatDate(row.publishedAt) : '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="130" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <!-- 全站公告对管理员 / 运营是只读参考：让他们知道平台发过什么，
                   免得重复发；但不给操作入口（后端三个写接口也会拦） -->
              <span v-if="!row.manageable" class="act act-disabled">只读</span>
              <el-dropdown v-else trigger="click" @command="cmd => handleAction(cmd, row)">
                <span class="act act-more">
                  操作<i class="el-icon-arrow-down" />
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="row.status === 0" command="publish">
                    <i class="el-icon-upload2" /> 发布
                  </el-dropdown-item>
                  <el-dropdown-item command="edit">
                    <i class="el-icon-edit" /> 编辑
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- ── 新增 / 编辑弹窗 ─────────────────────────────────── -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="92px">
        <el-form-item label="公告标题" prop="title">
          <el-input v-model="form.title" maxlength="60" show-word-limit placeholder="一句话说清这条公告讲什么" />
        </el-form-item>

        <el-form-item label="公告内容" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="6"
            placeholder="支持换行，用户端会按段落排版显示"
          />
        </el-form-item>

        <!-- 推送范围：不可撤回的动作，做成两张卡而不是下拉，防手滑 -->
        <el-form-item label="推送范围" prop="pubScope">
          <div class="scope-cards">
            <div
              class="scope-card"
              :class="{ 'scope-card--on': form.pubScope === 'ALL', 'scope-card--off': !isSuperAdmin }"
              @click="chooseScope('ALL')"
            >
              <div class="scope-card__t">
                <span class="scope-card__dot" />全站所有学校
              </div>
              <div class="scope-card__d">
                {{ isSuperAdmin ? '推给平台全部用户' : '仅超级管理员可用' }}
              </div>
            </div>
            <div
              class="scope-card"
              :class="{ 'scope-card--on': form.pubScope === 'SCHOOL' }"
              @click="chooseScope('SCHOOL')"
            >
              <div class="scope-card__t">
                <span class="scope-card__dot" />指定学校
              </div>
              <div class="scope-card__d">只推给选中的学校，可多选</div>
            </div>
          </div>

          <!-- 已发布后范围锁定：通知按旧范围发出去了，改它只会让两边对不上 -->
          <div class="field-note field-note--lock" v-if="rangeLocked">
            <i class="el-icon-lock" /> 公告已发布，推送范围不能再改。如需调整，请另发一条说明。
          </div>
        </el-form-item>

        <el-form-item label="推送学校" v-if="form.pubScope === 'SCHOOL'">
          <div class="school-picker" :class="{ 'school-picker--lock': rangeLocked }">
            <div class="school-grid" v-if="schoolOptions.length">
              <div
                v-for="item in schoolOptions"
                :key="item.id"
                class="sitem"
                :class="{ 'sitem--on': isSchoolSelected(item.id) }"
                @click="toggleSchool(item.id)"
              >
                <span class="sbox">{{ isSchoolSelected(item.id) ? '✓' : '' }}</span>
                <span class="sitem__n">{{ item.name }}</span>
                <span class="sitem__c" v-if="schoolUserCounts[item.id] !== undefined">
                  {{ schoolUserCounts[item.id] }}
                </span>
              </div>
            </div>
            <div class="school-empty" v-else>当前账号没有可管理的学校，请联系超级管理员授权。</div>

            <div class="picker-bar">
              <span>
                已选 <b>{{ form.schoolIds.length }}</b> 所 · 覆盖约
                <b :class="{ 'picker-bar__num--zero': form.schoolIds.length && reachCount === 0 }">
                  {{ formatNumber(reachCount) }}
                </b> 名用户
              </span>
              <span class="picker-bar__acts" v-if="!rangeLocked">
                <el-button type="text" @click="selectAllSchools">全选我的学校</el-button>
                <el-button type="text" @click="form.schoolIds = []">清空</el-button>
              </span>
            </div>

            <!-- 0 名用户必须有解释：只给一个"0"会让人以为是系统坏了 -->
            <div class="picker-note">
              <template v-if="!form.schoolIds.length">还没有选择学校。</template>
              <template v-else-if="reachCount === 0">
                所选的学校里暂时没有能收到公告的用户，请确认学校是否选错。
              </template>
              <template v-else>这个人数已排除正在封禁中与永久封禁的账号。</template>
            </div>
          </div>
        </el-form-item>

        <!-- 全站模式下也让人看见触达量 -->
        <el-form-item label="推送范围" v-if="form.pubScope === 'ALL'">
          <div class="all-reach">
            将推给 <b>{{ formatNumber(allUserCount) }}</b> 名用户（全站）
          </div>
          <div class="picker-note">
            已排除正在封禁中与永久封禁的账号；封禁已过期的账号仍在之内。
          </div>
        </el-form-item>

        <el-form-item label="落款单位">
          <el-input v-model="form.signatureName" maxlength="100" placeholder="如：兰州大学 学生工作部（可留空）" />
        </el-form-item>

        <el-form-item label="落款日期">
          <el-date-picker
            v-model="form.signatureDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="留空则用发布时间"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="发布人">
          <div class="publisher-locked">
            {{ currentPublisher }}
            <span class="publisher-locked__hint">取当前登录账号，不可改</span>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button class="ghost-btn" :loading="submitting" @click="submitForm(false)">
          {{ isEdit ? '保存（仍为草稿）' : '存为草稿' }}
        </el-button>
        <el-button
          v-if="canPublishAfterSave"
          type="primary"
          class="primary-btn"
          :loading="submitting"
          @click="submitForm(true)"
        >
          保存并发布
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getAnnouncementList,
  getAnnouncementDetail,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  publishAnnouncement,
  reachAnnouncement
} from '@/api/announcement'

export default {
  name: 'Announcement',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        schoolId: '',
        status: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      submitting: false,
      // 各可管学校的用户数，用于选择器上显示用量（后端一次返回）
      schoolUserCounts: {},
      allUserCount: 0,
      form: {
        id: null,
        title: '',
        content: '',
        pubScope: 'SCHOOL',
        schoolIds: [],
        signatureName: '',
        signatureDate: '',
        status: 0
      },
      rules: {
        title: [{ required: true, message: '请输入公告标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入公告内容', trigger: 'blur' }]
      }
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin', 'schoolOptions']),

    /** 页头那句说明 —— 明确告诉他这次能看到哪些学校，免得以为是系统少了数据 */
    scopeHint() {
      if (this.isSuperAdmin) {
        return '超级管理员可向全站或任意学校发布公告'
      }
      if (!this.schoolOptions.length) {
        return '当前账号还没有被授权任何学校，看不到任何公告'
      }
      return '当前账号可向已授权的 ' + this.schoolOptions.length + ' 所学校发布公告'
    },

    /** 已发布的公告不能改范围，也不能再发布一次 */
    rangeLocked() {
      return this.isEdit && this.form.status === 1
    },

    canPublishAfterSave() {
      return !(this.isEdit && this.form.status === 1)
    },

    /** 当前范围的预估触达人数：本地累加已选学校，全站用后端返回的总数 */
    reachCount() {
      if (this.form.pubScope === 'ALL') {
        return this.allUserCount
      }
      return this.form.schoolIds.reduce((sum, id) => sum + (this.schoolUserCounts[id] || 0), 0)
    },

    /** 发布人（只读展示，实际以后端登录态为准） */
    currentPublisher() {
      const info = this.$store.getters.adminInfo || {}
      return info.nickname || info.username || '当前账号'
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.listLoading = true
      getAnnouncementList(this.listQuery).then(res => {
        this.list = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },

    /** 拉一次触达用量：每校人数 + 全站总数，后面勾选时本地累加，不再反复请求 */
    fetchReach() {
      reachAnnouncement({ pubScope: 'SCHOOL', schoolIds: [] }).then(res => {
        const data = res.data || {}
        this.schoolUserCounts = data.schoolUserCounts || {}
        this.allUserCount = data.allUserCount || 0
      }).catch(() => {
        this.schoolUserCounts = {}
        this.allUserCount = 0
      })
    },

    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.schoolId = ''
      this.listQuery.status = ''
      this.handleFilter()
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleAction(cmd, row) {
      if (cmd === 'publish') return this.handlePublish(row)
      if (cmd === 'edit') return this.handleUpdate(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },

    isAllScope(row) {
      return row.pubScope !== 'SCHOOL'
    },
    isSchoolSelected(id) {
      return this.form.schoolIds.indexOf(id) >= 0
    },
    toggleSchool(id) {
      if (this.rangeLocked) return
      const index = this.form.schoolIds.indexOf(id)
      if (index >= 0) {
        this.form.schoolIds.splice(index, 1)
      } else {
        this.form.schoolIds.push(id)
      }
    },
    selectAllSchools() {
      if (this.rangeLocked) return
      this.form.schoolIds = this.schoolOptions.map(item => item.id)
    },
    chooseScope(scope) {
      if (this.rangeLocked) return
      if (scope === 'ALL' && !this.isSuperAdmin) return
      this.form.pubScope = scope
      // 切到「指定学校」时如果一所都没选，默认把他管的学校全带上 ——
      // 运营的授权范围本来就小，全选是最常见的操作
      if (scope === 'SCHOOL' && !this.form.schoolIds.length) {
        this.selectAllSchools()
      }
    },

    handleCreate() {
      this.resetForm()
      this.form.pubScope = 'SCHOOL'
      this.selectAllSchools()
      this.dialogTitle = '新增公告'
      this.isEdit = false
      this.dialogVisible = true
      this.fetchReach()
    },

    handleUpdate(row) {
      this.resetForm()
      this.isEdit = true
      this.form.id = row.id
      this.form.title = row.title
      this.form.content = row.content
      this.form.pubScope = this.isAllScope(row) ? 'ALL' : 'SCHOOL'
      this.form.schoolIds = []
      this.form.signatureName = row.signatureName || ''
      this.form.signatureDate = row.signatureDate || ''
      this.form.status = row.status
      this.dialogTitle = '编辑公告'
      this.dialogVisible = true
      this.fetchReach()
      // 列表只有学校名字，选中不了回选择器 —— 详情接口才有 schoolIds
      this.loadDetail(row.id)
    },

    loadDetail(id) {
      getAnnouncementDetail(id).then(res => {
        const data = res.data
        if (!data) return
        this.form.schoolIds = data.schoolIds || []
        this.form.signatureName = data.signatureName || ''
        this.form.signatureDate = data.signatureDate || ''
        this.form.status = data.status
      }).catch(() => {})
    },

    handleDelete(row) {
      const published = row.status === 1
      const message = published
        ? '删除后，用户消息里的这条公告也会一并消失，且无法恢复。'
        : '删除后无法恢复。'
      this.$confirm(message, published ? '删除并撤回这条公告？' : '删除这条草稿？', {
        confirmButtonText: '删除',
        type: 'warning'
      }).then(() => {
        deleteAnnouncement(row.id).then(res => {
          this.$message.success((res && res.message) || '删除成功')
          this.fetchData()
        })
      }).catch(() => {})
    },

    /**
     * 行内「发布」：先取触达人数，再让运营确认。
     * 公告发出去撤不回来，人数是唯一能让人停一下的东西。
     */
    handlePublish(row) {
      const payload = {
        pubScope: this.isAllScope(row) ? 'ALL' : 'SCHOOL',
        schoolIds: row.schoolIds || []
      }
      reachAnnouncement(payload).then(res => {
        const count = (res.data && res.data.userCount) || 0
        const rangeText = this.isAllScope(row) ? '全站所有学校' : (row.schoolNames || []).join('、')
        return this.$confirm(
          '将推送给「' + rangeText + '」共约 ' + this.formatNumber(count) +
          ' 名学生。发布后不能撤回，只能再发一条更正说明。',
          '确认把这条公告发出去？',
          { confirmButtonText: '确认发布', type: 'warning' }
        )
      }).then(() => {
        return publishAnnouncement(row.id)
      }).then(res => {
        if (!res) return
        this.$message.success((res && res.message) || '发布成功')
        this.fetchData()
      }).catch(() => {})
    },

    /**
     * 保存。withPublish=true 时保存成功后紧接着发布。
     * ------------------------------------------------------------------
     * 顺序刻意做成「先存草稿，再确认发布」：万一用户在确认框点了取消，
     * 内容已经落库了，不会白写一遍。新建会返回 id，直接拿去发布。
     */
    submitForm(withPublish) {
      this.$refs.form.validate(valid => {
        if (!valid) return

        if (this.form.pubScope === 'SCHOOL' && !this.form.schoolIds.length) {
          this.$message.warning('请至少选择一所推送学校')
          return
        }

        this.submitting = true
        const payload = {
          id: this.form.id,
          title: this.form.title,
          content: this.form.content,
          pubScope: this.form.pubScope,
          // 全站公告不传学校：pub_scope=ALL 时关联表必须为空，
          // 免得留下"既标了全站又挂着学校"的矛盾数据
          schoolIds: this.form.pubScope === 'SCHOOL' ? this.form.schoolIds : [],
          signatureName: this.form.signatureName,
          signatureDate: this.form.signatureDate || null
        }

        const saving = this.isEdit ? updateAnnouncement(payload) : createAnnouncement(payload)

        saving.then(res => {
          const announcementId = this.isEdit ? this.form.id : (res.data || this.form.id)
          this.$message.success(this.isEdit ? '保存成功' : '已存为草稿')
          this.fetchData()

          if (!withPublish) {
            this.submitting = false
            this.dialogVisible = false
            return
          }
          return this.confirmAndPublish(announcementId)
        }).catch(() => {
          this.submitting = false
        })
      })
    },

    confirmAndPublish(announcementId) {
      const payload = {
        pubScope: this.form.pubScope,
        schoolIds: this.form.pubScope === 'SCHOOL' ? this.form.schoolIds : []
      }
      return reachAnnouncement(payload).then(res => {
        const count = (res.data && res.data.userCount) || 0
        const rangeText = this.form.pubScope === 'ALL'
          ? '全站所有学校'
          : this.schoolOptions.filter(s => this.form.schoolIds.indexOf(s.id) >= 0).map(s => s.name).join('、')
        return this.$confirm(
          '将推送给「' + rangeText + '」共约 ' + this.formatNumber(count) +
          ' 名学生。发布后不能撤回，只能再发一条更正说明。',
          '确认把这条公告发出去？',
          { confirmButtonText: '确认发布', type: 'warning' }
        ).then(() => {
          return publishAnnouncement(announcementId)
        }).then(pubRes => {
          this.$message.success((pubRes && pubRes.message) || '发布成功')
          this.submitting = false
          this.dialogVisible = false
          this.fetchData()
        })
      }).catch(() => {
        // 用户点了「取消」：草稿已经存好，只是没发出去
        this.submitting = false
        this.dialogVisible = false
        this.$message.info('已存为草稿，未发布')
      })
    },

    resetForm() {
      this.form = {
        id: null,
        title: '',
        content: '',
        pubScope: 'SCHOOL',
        schoolIds: [],
        signatureName: '',
        signatureDate: '',
        status: 0
      }
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN', { hour12: false })
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0'
      return Number(value).toLocaleString('zh-CN')
    }
  }
}
</script>

<style lang="scss" scoped>
.announcement-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.publisher-name {
  margin: 0;
}

/* 推送范围 chips */
.scope-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.schip {
  font-size: 11px;
  background: #f4ece6;
  color: #3a2f3f;
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
}
.schip--all {
  background: #171219;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.schip--more {
  background: #fff;
  border: 1px solid #cfc6d1;
  color: #8c8191;
}
.schip--none {
  background: #fdf1f0;
  color: #c93b57;
}

/* 只读标记：全站公告对管理员 / 运营不给操作入口 */
.act-disabled {
  color: #a79ba9;
  cursor: not-allowed;
}

/* 推送范围单选卡 */
.scope-cards {
  display: flex;
  gap: 10px;
}
.scope-card {
  flex: 1;
  border: 1.5px solid #e8e0e6;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fcfbfa;
  cursor: pointer;
  transition: all 0.15s;
}
.scope-card--on {
  border-color: #c93b57;
  background: #fdf1f0;
}
.scope-card--off {
  opacity: 0.45;
  cursor: not-allowed;
}
.scope-card__t {
  font-size: 13px;
  font-weight: 700;
  color: #171219;
  display: flex;
  align-items: center;
  gap: 7px;
}
.scope-card__dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid #cfc6d1;
  flex-shrink: 0;
}
.scope-card--on .scope-card__dot {
  border: 4px solid #c93b57;
}
.scope-card__d {
  font-size: 11.5px;
  color: #8c8191;
  margin-top: 6px;
  line-height: 1.5;
}

/* 学校选择器：勾选网格 + 底部实时汇总 */
.school-picker {
  border: 1px solid #e8e0e6;
  border-radius: 10px;
  background: #fcfbfa;
  padding: 12px;
}
.school-picker--lock {
  opacity: 0.6;
  pointer-events: none;
}
.school-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sitem {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid #e8e0e6;
  border-radius: 8px;
  padding: 7px 11px;
  font-size: 12.5px;
  color: #3a2f3f;
  cursor: pointer;
  transition: all 0.15s;
}
.sitem--on {
  border-color: #c93b57;
  background: #fdf1f0;
  font-weight: 600;
  color: #171219;
}
.sbox {
  width: 13px;
  height: 13px;
  border-radius: 3px;
  border: 1.5px solid #cfc6d1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: #fff;
  line-height: 1;
}
.sitem--on .sbox {
  background: #c93b57;
  border-color: #c93b57;
}
.sitem__c {
  font-size: 11px;
  color: #a79ba9;
}
.school-empty {
  font-size: 12.5px;
  color: #8c8191;
  padding: 6px 2px;
}
.picker-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 11px;
  border-top: 1px dashed #e8e0e6;
  font-size: 12px;
  color: #8c8191;
}
.picker-bar b {
  color: #c93b57;
  font-weight: 700;
}
/* 人数为 0 时数字转警示色 —— 让人先看见"0"，再看下面的原因说明 */
.picker-bar__num--zero {
  color: #ba7517 !important;
}
.picker-bar__acts {
  margin-left: auto;
}

/* 人数口径说明：只在有数字的时候出现，避免占地方 */
.picker-note {
  font-size: 11.5px;
  color: #8c8191;
  line-height: 1.6;
  margin-top: 8px;
}

/* 全站触达提示 */
.all-reach {
  font-size: 13px;
  color: #3a2f3f;
  background: #f7f5f4;
  border-radius: 8px;
  padding: 10px 14px;
}
.all-reach b {
  color: #c93b57;
  font-weight: 700;
}

/* 已发布后范围锁定提示 */
.field-note {
  font-size: 11.5px;
  color: #8c8191;
  line-height: 1.6;
  margin-top: 8px;
}
.field-note--lock {
  color: #ba7517;
}

/* 发布人只读展示 */
.publisher-locked {
  font-size: 13px;
  color: #171219;
  background: #f7f5f4;
  border-radius: 8px;
  padding: 9px 14px;
}
.publisher-locked__hint {
  font-size: 11.5px;
  color: #8c8191;
  margin-left: 8px;
}
</style>
